import { useMemo } from "react";
import _ from "lodash";

import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";

import { getDiscountPriceFor } from "utils/planUtils";

import { Coupon } from "types/Coupons";
import Plan from "types/Plan";


function mergeCoupons(coupons: Coupon[] | null | undefined, couponsToMerge: Coupon | Coupon[] | undefined): Coupon[] | undefined {
  // TODO add logic here to match Chargebee's behavior
  //  only one coupon of the same kind can be active at a time, so e.g. if there's already an active -20% coupon
  //  and the user enters a code for another % coupon, that should replace the existing one and should not get added
  //  on top of that
  const couponsToMergeArray = Array.isArray(couponsToMerge) ? couponsToMerge : (couponsToMerge ? [ couponsToMerge ] : undefined);

  if (coupons) {
    if (couponsToMergeArray) {
      return _.unionWith(coupons, couponsToMergeArray, (c1: Coupon, c2: Coupon) => { return c1.coupon_code === c2.coupon_code && c1.coupon_type === c2.coupon_type; });
    } else {
      return coupons;
    }
  } else {
    return couponsToMergeArray;
  }
}

type UseDiscountPriceArgs = {
  useScheduledSubscriptionIfAny?: boolean;
  coupon?: Coupon;
  targetPlan?: Pick<Plan, "planSlug" | "price" | "defaultCoupons">;
  /** Skip active subscription coupons — only use defaultCoupons + the passed coupon. */
  skipActiveCoupons?: boolean;
};

// BUG: Discount prices shown in the UI may not match Chargebee invoices.
// The frontend calculates discounts from locally-stored coupons (DB), but invoices
// are generated by Chargebee using its own coupon state. If the two are out of sync
// (e.g. coupon removed in Chargebee but still in DB, or vice versa), the user sees
// a discounted price in the UI but full price on their downloadable invoice.
// Fix requires coupon reconcile-on-read between DB and Chargebee — see project memory.
export function useDiscountPrice(args?: UseDiscountPriceArgs) {
  const { activeSubscription } = useActiveSubscription();
  const { activeCoupons } = useActiveCoupons();

  const target = useMemo(() => {
    if (args?.targetPlan) {
      return args.targetPlan;
    }

    if (args?.useScheduledSubscriptionIfAny && activeSubscription?.scheduledSubscription) {
      return activeSubscription.scheduledSubscription;
    }

    return activeSubscription;
  }, [ activeSubscription, args ]);

  const discountPrice = useMemo(() => {
    if (args?.skipActiveCoupons) {
      return getDiscountPriceFor(target, mergeCoupons(target?.defaultCoupons, args?.coupon));
    }
    return getDiscountPriceFor(target, mergeCoupons(target?.defaultCoupons, mergeCoupons(activeCoupons, args?.coupon)));
  }, [ target, activeCoupons, args ]);

  return discountPrice;
}
